import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import GoogleMapsLocationPicker from './GoogleMapsLocationPicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faMapMarkerAlt, faCheck, faTimes, faSearch, faGlobe, faPhone, faLink, faInfoCircle, faMap, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import './VenueManagement.css';

/**
 * VenueManagement Component
 * 
 * Allows admins to:
 * - View all venues
 * - Add new venues
 * - Edit existing venues
 * - Delete venues (soft delete)
 * - Search and filter venues
 */
const VenueManagement = () => {
  const { getAccessToken } = useAuth();
  
  // State
  const [venues, setVenues] = useState([]);
  const [filteredVenues, setFilteredVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // UI State
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedVenueForMap, setSelectedVenueForMap] = useState(null);
  
  // Form State
  const [venueData, setVenueData] = useState(null);
  const [venueNotes, setVenueNotes] = useState('');
  const [venueName, setVenueName] = useState('');
  const [preserveVenueName, setPreserveVenueName] = useState(false); // Flag to preserve venue name during edit
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch venues on mount
  useEffect(() => {
    fetchVenues();
  }, []);

  // Filter venues based on search
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredVenues(venues);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = venues.filter(venue =>
        venue.name.toLowerCase().includes(query) ||
        venue.city?.toLowerCase().includes(query) ||
        venue.state?.toLowerCase().includes(query) ||
        venue.formattedAddress?.toLowerCase().includes(query)
      );
      setFilteredVenues(filtered);
    }
  }, [searchQuery, venues]);

  /**
   * Fetch all venues
   */
  const fetchVenues = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/em/v1/venues`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch venues');
      }

      const data = await response.json();
      if (data.status === 'success') {
        setVenues(data.data || []);
        setFilteredVenues(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to load venues');
      }

    } catch (err) {
      console.error('Error fetching venues:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle location selection from LocationPicker
   */
  const handleLocationSelect = (locationData) => {
    if (locationData) {
      setVenueData({
        name: locationData.name,
        formattedAddress: locationData.formattedAddress,
        address: locationData.address,
        city: locationData.city,
        state: locationData.state,
        country: locationData.country,
        countryCode: locationData.countryCode,
        postalCode: locationData.postalCode,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        timezone: locationData.timezone,
        placeId: locationData.placeId,
        types: locationData.types ? locationData.types.join(',') : '',
        phoneNumber: locationData.phoneNumber,
        website: locationData.website,
        googleMapsUrl: locationData.url,
        rating: locationData.rating,
        userRatingsTotal: locationData.userRatingsTotal
      });
      
      // Only update venue name if:
      // 1. We're adding a new venue (not editing), OR
      // 2. We're editing but should NOT preserve the current name (user explicitly selected new location)
      if (!preserveVenueName) {
        setVenueName(locationData.name || '');
      }
      
      // After first selection, allow name updates
      if (preserveVenueName) {
        setPreserveVenueName(false);
      }
    } else {
      setVenueData(null);
      if (!preserveVenueName) {
        setVenueName('');
      }
    }
  };

  /**
   * Create a new venue
   */
  const handleCreateVenue = async () => {
    if (!venueData) {
      setError('Please select a location first');
      return;
    }

    // Validate venue name
    if (!venueName || venueName.trim() === '') {
      setError('Venue name is required');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const venueRequest = {
        ...venueData,
        name: venueName.trim(), // Use custom name provided by user
        notes: venueNotes,
        active: true
      };

      const response = await fetch(`${config.API_BASE_URL}/em/v1/venues`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(venueRequest)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create venue');
      }

      const data = await response.json();
      if (data.status === 'success') {
        setSuccessMessage('Venue created successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // Reset form
        setVenueData(null);
        setVenueNotes('');
        setVenueName('');
        setPreserveVenueName(false);
        setShowAddForm(false);
        
        // Refresh venues list
        fetchVenues();
      } else {
        throw new Error(data.message || 'Failed to create venue');
      }

    } catch (err) {
      console.error('Error creating venue:', err);
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Open edit form with venue data
   */
  const handleEditVenue = (venue) => {
    setEditingVenue(venue);
    setVenueData({
      name: venue.name,
      formattedAddress: venue.formattedAddress,
      address: venue.address,
      city: venue.city,
      state: venue.state,
      country: venue.country,
      countryCode: venue.countryCode,
      postalCode: venue.postalCode,
      latitude: venue.latitude,
      longitude: venue.longitude,
      timezone: venue.timezone,
      placeId: venue.placeId,
      types: venue.types,
      phoneNumber: venue.phoneNumber,
      website: venue.website,
      googleMapsUrl: venue.googleMapsUrl,
      rating: venue.rating,
      userRatingsTotal: venue.userRatingsTotal
    });
    setVenueName(venue.name || '');
    setVenueNotes(venue.notes || '');
    setPreserveVenueName(true); // Preserve the current venue name initially
    setShowEditForm(true);
  };

  /**
   * Update existing venue
   */
  const handleUpdateVenue = async () => {
    if (!venueData) {
      setError('Please select a location first');
      return;
    }

    // Validate venue name
    if (!venueName || venueName.trim() === '') {
      setError('Venue name is required');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const venueRequest = {
        ...venueData,
        name: venueName.trim(), // Use custom name provided by user
        notes: venueNotes,
        active: true
      };

      const response = await fetch(`${config.API_BASE_URL}/em/v1/venues/${editingVenue.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(venueRequest)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update venue');
      }

      const data = await response.json();
      if (data.status === 'success') {
        setSuccessMessage('Venue updated successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // Reset form
        setVenueData(null);
        setVenueNotes('');
        setVenueName('');
        setPreserveVenueName(false);
        setEditingVenue(null);
        setShowEditForm(false);
        
        // Refresh venues list
        fetchVenues();
      } else {
        throw new Error(data.message || 'Failed to update venue');
      }

    } catch (err) {
      console.error('Error updating venue:', err);
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Delete venue (soft delete)
   */
  const handleDeleteVenue = async (venueId, venueName) => {
    if (!window.confirm(`Are you sure you want to delete "${venueName}"? This will mark it as inactive.`)) {
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/em/v1/venues/${venueId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete venue');
      }

      setSuccessMessage('Venue deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Refresh venues list
      fetchVenues();

    } catch (err) {
      console.error('Error deleting venue:', err);
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Cancel form
   */
  const handleCancelForm = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setEditingVenue(null);
    setVenueData(null);
    setVenueNotes('');
    setVenueName('');
    setPreserveVenueName(false);
    setError(null);
  };

  /**
   * View venue on map
   */
  const handleViewOnMap = (venue) => {
    setSelectedVenueForMap(venue);
    setShowMapModal(true);
  };

  /**
   * Close map modal
   */
  const handleCloseMapModal = () => {
    setShowMapModal(false);
    setSelectedVenueForMap(null);
  };

  return (
    <div className="venue-management-container">
      <div className="venue-management-header">
        <h2>📍 Venue Management</h2>
        <button 
          className="add-venue-btn" 
          onClick={() => setShowAddForm(true)}
          disabled={showAddForm || showEditForm}
        >
          <FontAwesomeIcon icon={faPlus} /> Add New Venue
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="venue-success-alert">
          <FontAwesomeIcon icon={faCheck} /> {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="venue-error-alert">
          <FontAwesomeIcon icon={faTimes} /> {error}
        </div>
      )}

      {/* Add Venue Form */}
      {showAddForm && (
        <div className="venue-form-card">
          <div className="venue-form-header">
            <h3>Add New Venue</h3>
            <button className="close-form-btn" onClick={handleCancelForm}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="venue-form-body">
            <div className="form-group">
              <label>Search for Venue Location *</label>
              <GoogleMapsLocationPicker
                onLocationSelect={handleLocationSelect}
                placeholder="Search for venue or click on the map..."
              />
            </div>

            {venueData && (
              <>
                <div className="form-group">
                  <label htmlFor="venue-name">Venue Name *</label>
                  <input
                    id="venue-name"
                    type="text"
                    className="venue-name-input"
                    value={venueName}
                    onChange={(e) => setVenueName(e.target.value)}
                    placeholder="Enter venue name"
                    required
                  />
                  <small className="form-hint">
                    You can customize the venue name or keep the name from Google Maps
                  </small>
                </div>

                <div className="venue-preview-card">
                  <h4>
                    📍 {venueName || venueData.name}
                    {venueData.rating && (
                      <span className="venue-rating-badge">
                        ⭐ {venueData.rating}
                        {venueData.userRatingsTotal && ` (${venueData.userRatingsTotal})`}
                      </span>
                    )}
                  </h4>
                  <p className="venue-preview-address">
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> {venueData.formattedAddress}
                  </p>
                  <div className="venue-preview-meta">
                    {venueData.city && venueData.state && (
                      <span>📍 {venueData.city}, {venueData.state}</span>
                    )}
                    <span>🌍 {venueData.timezone}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="venue-notes">Additional Notes (Optional)</label>
                  <textarea
                    id="venue-notes"
                    className="venue-notes-textarea"
                    rows="3"
                    value={venueNotes}
                    onChange={(e) => setVenueNotes(e.target.value)}
                    placeholder="Add any notes about this venue (parking info, entrance details, etc.)"
                  />
                </div>

                <div className="venue-form-actions">
                  <button 
                    className="cancel-btn" 
                    onClick={handleCancelForm}
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    className="submit-btn" 
                    onClick={handleCreateVenue}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Creating...' : <><FontAwesomeIcon icon={faCheck} /> Create Venue</>}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Venue Form */}
      {showEditForm && editingVenue && (
        <div className="venue-form-card">
          <div className="venue-form-header">
            <h3>Edit Venue: {editingVenue.name}</h3>
            <button className="close-form-btn" onClick={handleCancelForm}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="venue-form-body">
            <div className="form-group">
              <label>Search for New Venue Location *</label>
              <GoogleMapsLocationPicker
                onLocationSelect={handleLocationSelect}
                initialLocation={venueData ? {
                  latitude: venueData.latitude,
                  longitude: venueData.longitude
                } : null}
                placeholder="Search for venue or click on the map..."
              />
            </div>

            {venueData && (
              <>
                <div className="form-group">
                  <label htmlFor="edit-venue-name">Venue Name *</label>
                  <input
                    id="edit-venue-name"
                    type="text"
                    className="venue-name-input"
                    value={venueName}
                    onChange={(e) => setVenueName(e.target.value)}
                    placeholder="Enter venue name"
                    required
                  />
                  <small className="form-hint">
                    You can customize the venue name or keep the name from Google Maps
                  </small>
                </div>

                <div className="venue-preview-card">
                  <h4>
                    📍 {venueName || venueData.name}
                    {venueData.rating && (
                      <span className="venue-rating-badge">
                        ⭐ {venueData.rating}
                        {venueData.userRatingsTotal && ` (${venueData.userRatingsTotal})`}
                      </span>
                    )}
                  </h4>
                  <p className="venue-preview-address">
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> {venueData.formattedAddress}
                  </p>
                  <div className="venue-preview-meta">
                    {venueData.city && venueData.state && (
                      <span>📍 {venueData.city}, {venueData.state}</span>
                    )}
                    <span>🌍 {venueData.timezone}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-venue-notes">Additional Notes (Optional)</label>
                  <textarea
                    id="edit-venue-notes"
                    className="venue-notes-textarea"
                    rows="3"
                    value={venueNotes}
                    onChange={(e) => setVenueNotes(e.target.value)}
                    placeholder="Add any notes about this venue (parking info, entrance details, etc.)"
                  />
                </div>

                <div className="venue-form-actions">
                  <button 
                    className="cancel-btn" 
                    onClick={handleCancelForm}
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    className="submit-btn" 
                    onClick={handleUpdateVenue}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Updating...' : <><FontAwesomeIcon icon={faCheck} /> Update Venue</>}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Venues List */}
      {!showAddForm && !showEditForm && (
        <>
          {/* Search Bar */}
          <div className="venue-search-bar">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search venues by name, city, or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="venue-search-input"
            />
            {searchQuery && (
              <button 
                className="clear-search-btn" 
                onClick={() => setSearchQuery('')}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>

          {/* Venues Grid */}
          {loading ? (
            <div className="venue-loading">Loading venues...</div>
          ) : filteredVenues.length === 0 ? (
            <div className="venue-empty-state">
              <FontAwesomeIcon icon={faMapMarkerAlt} size="3x" />
              <h3>{searchQuery ? 'No venues found' : 'No venues yet'}</h3>
              <p>
                {searchQuery 
                  ? 'Try a different search term'
                  : 'Create your first venue to get started'}
              </p>
            </div>
          ) : (
            <div className="venues-grid">
              {filteredVenues.map((venue) => (
                <div key={venue.id} className="venue-card">
                  <div className="venue-card-header">
                    <h3>
                      {venue.name}
                      {venue.rating && (
                        <span className="venue-card-rating">
                          ⭐ {venue.rating}
                        </span>
                      )}
                    </h3>
                  </div>

                  <div className="venue-card-body">
                    <p className="venue-card-address">
                      <FontAwesomeIcon icon={faMapMarkerAlt} /> 
                      {venue.formattedAddress}
                    </p>

                    {venue.city && venue.state && (
                      <p className="venue-card-location">
                        📍 {venue.city}, {venue.state} {venue.postalCode}
                      </p>
                    )}

                    <p className="venue-card-timezone">
                      <FontAwesomeIcon icon={faGlobe} /> {venue.timezone}
                    </p>

                    {venue.phoneNumber && (
                      <p className="venue-card-phone">
                        <FontAwesomeIcon icon={faPhone} /> {venue.phoneNumber}
                      </p>
                    )}

                    {venue.website && (
                      <p className="venue-card-website">
                        <FontAwesomeIcon icon={faLink} />{' '}
                        <a href={venue.website} target="_blank" rel="noopener noreferrer">
                          Visit Website
                        </a>
                      </p>
                    )}

                    {venue.notes && (
                      <p className="venue-card-notes">
                        <FontAwesomeIcon icon={faInfoCircle} /> {venue.notes}
                      </p>
                    )}
                  </div>

                  <div className="venue-card-actions">
                    <button 
                      className="view-map-venue-btn"
                      onClick={() => handleViewOnMap(venue)}
                      disabled={actionLoading}
                    >
                      <FontAwesomeIcon icon={faMap} /> View on Map
                    </button>
                    <button 
                      className="edit-venue-btn"
                      onClick={() => handleEditVenue(venue)}
                      disabled={actionLoading}
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edit
                    </button>
                    <button 
                      className="delete-venue-btn"
                      onClick={() => handleDeleteVenue(venue.id, venue.name)}
                      disabled={actionLoading}
                    >
                      <FontAwesomeIcon icon={faTrash} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Map View Modal */}
      {showMapModal && selectedVenueForMap && (
        <div className="venue-map-modal" onClick={handleCloseMapModal}>
          <div className="venue-map-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="venue-map-modal-header">
              <div className="venue-map-modal-title">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <h3>{selectedVenueForMap.name}</h3>
              </div>
              <button 
                className="close-map-modal-btn"
                onClick={handleCloseMapModal}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="venue-map-modal-body">
              {/* Venue Details Summary */}
              <div className="venue-map-details">
                <div className="venue-map-detail-item">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="detail-icon" />
                  <div>
                    <strong>Address:</strong>
                    <p>{selectedVenueForMap.formattedAddress}</p>
                  </div>
                </div>

                {selectedVenueForMap.city && selectedVenueForMap.state && (
                  <div className="venue-map-detail-item">
                    <FontAwesomeIcon icon={faGlobe} className="detail-icon" />
                    <div>
                      <strong>Location:</strong>
                      <p>{selectedVenueForMap.city}, {selectedVenueForMap.state} {selectedVenueForMap.postalCode}</p>
                    </div>
                  </div>
                )}

                <div className="venue-map-detail-item">
                  <FontAwesomeIcon icon={faGlobe} className="detail-icon" />
                  <div>
                    <strong>Timezone:</strong>
                    <p>{selectedVenueForMap.timezone}</p>
                  </div>
                </div>

                {selectedVenueForMap.phoneNumber && (
                  <div className="venue-map-detail-item">
                    <FontAwesomeIcon icon={faPhone} className="detail-icon" />
                    <div>
                      <strong>Phone:</strong>
                      <p>{selectedVenueForMap.phoneNumber}</p>
                    </div>
                  </div>
                )}

                {selectedVenueForMap.rating && (
                  <div className="venue-map-detail-item">
                    <span className="detail-icon">⭐</span>
                    <div>
                      <strong>Rating:</strong>
                      <p>{selectedVenueForMap.rating} / 5.0 {selectedVenueForMap.userRatingsTotal && `(${selectedVenueForMap.userRatingsTotal} reviews)`}</p>
                    </div>
                  </div>
                )}

                {/* External Links */}
                <div className="venue-map-external-links">
                  {selectedVenueForMap.website && (
                    <a 
                      href={selectedVenueForMap.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="venue-external-link"
                    >
                      <FontAwesomeIcon icon={faLink} /> Visit Website
                    </a>
                  )}
                  {selectedVenueForMap.googleMapsUrl && (
                    <a 
                      href={selectedVenueForMap.googleMapsUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="venue-external-link"
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} /> Open in Google Maps
                    </a>
                  )}
                  {selectedVenueForMap.latitude && selectedVenueForMap.longitude && !selectedVenueForMap.googleMapsUrl && (
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${selectedVenueForMap.latitude},${selectedVenueForMap.longitude}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="venue-external-link"
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} /> Open in Google Maps
                    </a>
                  )}
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="venue-map-container">
                {selectedVenueForMap.latitude && selectedVenueForMap.longitude ? (
                  <iframe
                    title={`Map of ${selectedVenueForMap.name}`}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://www.google.com/maps/embed/v1/place?key=${config.GOOGLE_MAPS_API_KEY}&q=${selectedVenueForMap.latitude},${selectedVenueForMap.longitude}&zoom=15&maptype=roadmap`}
                    allowFullScreen
                  />
                ) : (
                  <div className="venue-map-unavailable">
                    <FontAwesomeIcon icon={faMapMarkerAlt} size="3x" />
                    <p>Map coordinates not available for this venue</p>
                  </div>
                )}
              </div>
            </div>

            <div className="venue-map-modal-footer">
              <button 
                className="close-map-btn"
                onClick={handleCloseMapModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VenueManagement;

